import { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import type {
  ActiveJob, WorkspaceOverview, WorkspaceSection, FeatureFlag, TenantBanner,
  TenantFeatures, TenantStatus,
} from '../lib/types';
import { apiUrl } from '../lib/consts';

interface WorkspaceContextType {
  workspaceName: string;
  setWorkspaceName: (name: string) => void;
  logoUrl: string;
  sections: WorkspaceSection[];
  fields: Record<string, any>;
  activeJobs: ActiveJob[];
  hasActiveJobs: boolean;
  overviewLoading: boolean;
  // 403s from regional remain the source of truth; this avoids dead-end clicks.
  status: TenantStatus;
  isReadOnly: boolean;
  features: TenantFeatures;
  hasFeature: (flag: FeatureFlag) => boolean;
  featureValue: <T = unknown>(flag: FeatureFlag, key: string, fallback?: T) => T | undefined;
  // Dismiss removes optimistically; the next poll restores a banner whose dismissal failed.
  banners: TenantBanner[];
  dismissBanner: (id: string) => Promise<void>;
  saveField: (fieldId: string, value: any) => Promise<void>;
  refresh: () => Promise<void>;
  sources: WorkspaceOverview['sources'];
  chats: WorkspaceOverview['chats'];
  agents: WorkspaceOverview['agents'];
  unreadNotifications: number;
  reviewItems: number;
}

const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

const DEFAULT_LOGO = '/logo.png';

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { token, authFetch } = useAuth();
  const [workspaceName, setWorkspaceName] = useState('');
  const [logoUrl, setLogoUrl] = useState(DEFAULT_LOGO);

  const [sections, setSections] = useState<WorkspaceSection[]>([]);
  const [fields, setFields] = useState<Record<string, any>>({});
  const [activeJobs, setActiveJobs] = useState<ActiveJob[]>([]);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [features, setFeatures] = useState<TenantFeatures>({});
  const [status, setStatus] = useState<TenantStatus>('active');
  const [banners, setBanners] = useState<TenantBanner[]>([]);
  const [sources, setSources] = useState<WorkspaceOverview['sources']>([]);
  const [chats, setChats] = useState<WorkspaceOverview['chats']>([]);
  const [agents, setAgents] = useState<WorkspaceOverview['agents']>([]);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [reviewItems, setReviewItems] = useState(0);

  // Coalesce concurrent refresh() calls so /workspace/overview is fetched once at a time.
  const inFlight = useRef<Promise<void> | null>(null);
  const refresh = useCallback(async () => {
    if (!token) return;
    if (inFlight.current) return inFlight.current;
    const p = (async () => {
      try {
        const res = await authFetch('/workspace');
        if (!res.ok) return;
        const data: WorkspaceOverview = await res.json();
        if (data.name) setWorkspaceName(data.name);
        setLogoUrl(data.has_logo ? apiUrl + '/workspace/logo' : DEFAULT_LOGO);
        setSections(data.sections ?? []);
        setFields(data.fields ?? {});
        setActiveJobs(data.active_jobs ?? []);
        setFeatures(data.features ?? {});
        setStatus(data.status ?? 'active');
        setBanners(data.banners ?? []);
        setSources(data.sources ?? []);
        setChats(data.chats ?? []);
        setAgents(data.agents ?? []);
        setUnreadNotifications(data.unread_notifications ?? 0);
        setReviewItems(data.review_items ?? 0);
      } finally {
        setOverviewLoading(false);
      }
    })();
    inFlight.current = p;
    try { await p; } finally { inFlight.current = null; }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setOverviewLoading(false);
      return;
    }
    refresh();
  }, [token, refresh]);

  useEffect(() => {
    if (!token) return;
    const tick = () => {
      if (document.visibilityState === 'visible') refresh();
    };
    const interval = setInterval(tick, 5_000);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [token, refresh]);

  const saveField = useCallback(async (fieldId: string, value: any) => {
    // Optimistic update; refresh() then pulls the canonical state, including newly-(in)relevant sections.
    setFields(prev => ({ ...prev, [fieldId]: value }));
    const res = await authFetch(`/workspace/fields/${fieldId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value }),
    });
    if (!res.ok) throw new Error('Failed to save field');
    await refresh();
  }, [refresh]);

  const hasFeature = useCallback((flag: FeatureFlag) => {
    const val = features[flag];
    return val !== undefined && val !== false && val !== null;
  }, [features]);

  const featureValue = useCallback(<T = unknown,>(
    flag: FeatureFlag, key: string, fallback?: T,
  ): T | undefined => {
    const val = features[flag];
    if (val && typeof val === 'object') {
      const v = (val as Record<string, unknown>)[key];
      return v === undefined ? fallback : (v as T);
    }
    return fallback;
  }, [features]);

  const isReadOnly = status === 'past_due' || status === 'archived';

  const dismissBanner = useCallback(async (id: string) => {
    setBanners(prev => prev.filter(b => b.id !== id));
    try {
      await authFetch(`/banners/${id}/dismiss`, { method: 'POST' });
    } catch {
      refresh();
    }
  }, [authFetch, refresh]);

  return (
    <WorkspaceContext.Provider
      value={{
        workspaceName, setWorkspaceName, logoUrl,
        sections, fields, activeJobs, hasActiveJobs: activeJobs.length > 0, overviewLoading,
        status, isReadOnly, features, hasFeature, featureValue,
        banners, dismissBanner,
        saveField, refresh,
        sources, chats, agents, unreadNotifications, reviewItems,
      }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
}
